/* global OfficeRuntime, process */

import { PublicClientApplication } from '@azure/msal-browser';
import { DEBUG_MODE, TOKEN_CACHE_DURATION_MS } from './constants';

interface CachedToken {
  token: string;
  expiresAt: number;
}

class TokenCache {
  private cachedToken: CachedToken | null = null;
  private msalInstance: PublicClientApplication | null = null;
  private inFlightToken: Promise<string> | null = null;
  
  async getAccessToken(): Promise<string> {
    if (DEBUG_MODE) {
      return "local-dev-office-token";
    }

    if (this.cachedToken && Date.now() < this.cachedToken.expiresAt) {
      return this.cachedToken.token;
    }

    if (this.inFlightToken) {
      return this.inFlightToken;
    }

    this.inFlightToken = this.fetchAccessToken();

    try {
      return await this.inFlightToken;
    } finally {
      this.inFlightToken = null;
    }
  }

  private async fetchAccessToken(): Promise<string> {
    let token: string;

    try {
      token = await OfficeRuntime.auth.getAccessToken();
    } catch (officeError) {
      token = await this.getMSALToken();
    }

    this.cachedToken = {
      token,
      expiresAt: Date.now() + TOKEN_CACHE_DURATION_MS,
    };
    
    return token;
  }

  private async getMSALToken(): Promise<string> {
    if (!this.msalInstance) {
      await this.initializeMSAL();
    }

    if (!this.msalInstance) {
      throw new Error("MSAL instance not initialized");
    }

    const accounts = this.msalInstance.getAllAccounts();
    const clientId = process.env.APP_REGISTRATION_ID || '';
    const redirectUri = process.env.APP_AUTH_REDIRECT || 'https://localhost:3000';
    const host = redirectUri.replace(/^https?:\/\//, '').split('/')[0];
    const scope = `api://${host}/${clientId}/access_as_user`;

    try {
      if (accounts.length > 0) {
        const response = await this.msalInstance.acquireTokenSilent({
          scopes: [scope],
          account: accounts[0],
        });
        return response.accessToken;
      }
    } catch (silentError) {
      // Fall through to interactive auth
    }

    const response = await this.msalInstance.acquireTokenPopup({
      scopes: [scope],
      prompt: 'select_account',
    });
    return response.accessToken;
  }

  private async initializeMSAL(): Promise<void> {
    const clientId = process.env.APP_REGISTRATION_ID;
    const authority = process.env.APP_AUTH_AUTHORITY || 'https://login.microsoftonline.com/common';
    const redirectUri = process.env.APP_AUTH_REDIRECT || 'https://localhost:3000';
    
    if (!clientId) {
      throw new Error('APP_REGISTRATION_ID environment variable is required');
    }
    
    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    });

    await this.msalInstance.initialize();
  }

  async clearCache(): Promise<void> {
    this.cachedToken = null;
    this.inFlightToken = null;

    if (this.msalInstance) {
      const accounts = this.msalInstance.getAllAccounts();
      for (const account of accounts) {
        try {
          await this.msalInstance.logoutPopup({ account });
        } catch (error) {
          // Logout failed, continue
        }
      }
    }
  }

  isTokenCached(): boolean {
    return this.cachedToken !== null && Date.now() < this.cachedToken.expiresAt;
  }

  getAuthStatus(): { hasCachedToken: boolean; msalAccounts: number } {
    return {
      hasCachedToken: this.isTokenCached(),
      msalAccounts: this.msalInstance ? this.msalInstance.getAllAccounts().length : 0,
    };
  }
}

// Export singleton instance
export const tokenCache = new TokenCache();
